/**
 * useCreditSync — Bridge between AuthContext (Firestore onSnapshot) and creditStore (Zustand).
 *
 * Place this hook once in App.tsx or a top-level component.
 * It watches userAccount.credits from AuthContext and syncs to the credit store
 * so all UI components get reactive credit balance updates across browser windows.
 */

import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCreditStore } from '../stores/creditStore';
import type { FirestoreCredits } from '../stores/creditStore';

export function useCreditSync(): void {
  const { user, userAccount } = useAuth();
  const syncFromFirestore = useCreditStore((s) => s.syncFromFirestore);
  const reset = useCreditStore((s) => s.reset);

  useEffect(() => {
    if (!user) {
      // Logged out — reset credit store
      reset();
      return;
    }

    // Sync credits from Firestore snapshot (real-time via AuthContext onSnapshot)
    const credits = userAccount?.credits as FirestoreCredits | undefined;
    syncFromFirestore(credits, true);
  }, [user, userAccount?.credits, syncFromFirestore, reset]);
}
